import React, { useEffect } from "react";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, Tooltip } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import { ReactComponent as DetermineArchetypeSVG } from "../../asserts/logo/determine.svg";
import { ReactComponent as WeightsSVG } from "../../asserts/logo/weights.svg";
import { ReactComponent as RisksSVG } from "../../asserts/logo/risks.svg";
import { ReactComponent as MetricsSVG } from "../../asserts/logo/metrics.svg";
import { ReactComponent as DatasetGenSVG } from "../../asserts/logo/datasetgen.svg";
import { ReactComponent as TestLabSVG } from "../../asserts/logo/testlab.svg";
import { ReactComponent as EvidenceSVG } from "../../asserts/logo/evidence.svg";
import { ReactComponent as DashboardSVG } from "../../asserts/logo/dashboard.svg";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import FunctionsOutlinedIcon from "@mui/icons-material/FunctionsOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import DonutSmallOutlinedIcon from "@mui/icons-material/DonutSmallOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSidebar } from "./DashboardLayout";

function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { projectId } = useParams();
  const { isSidebarExpanded, setIsSidebarExpanded, sidebarWidth, collapsedWidth, expandedWidth } = useSidebar();

  const modules = [
    
    { label: "Determine AI Archetype", icon: <DetermineArchetypeSVG/>, path: "application-setup" },
    { label: "Set Trust Index Weights", icon: <WeightsSVG />, path: "trust-index-settings" },
    { label: "Identify Risks", icon: <RisksSVG />, path: "risk-identification" },
    { label: "Define Metrics", icon: <MetricsSVG />, path: "metrics-definition" },
    { label: "Generate Test Suite", icon: <DatasetGenSVG />, path: "usecase-dataset-generation" },
    { label: "Test Execution", icon: <TestLabSVG />, path: "test-evaluation" },
    { label: "Compute Trust Score", icon: <EvidenceSVG />, path: "test-score-computation" },
    { label: "Evaluation Dashboard", icon: <DashboardSVG />, path: "evaluation-dashboard" },
  ];

  // Handle module click - expand sidebar when clicking a module
  const handleModuleClick = (modulePath) => {
    setIsSidebarExpanded(true);
    navigate(`/project/${projectId}/${modulePath}`);
  };

  // Handle click outside sidebar to collapse (when expanded)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.getElementById('sidebar-container');
      // Collapse if clicking outside the sidebar and sidebar is expanded
      if (sidebar && !sidebar.contains(event.target) && isSidebarExpanded) {
        setIsSidebarExpanded(false);
      }
    };

    // Only add listener when sidebar is expanded
    if (isSidebarExpanded) {
      // Use a small delay to avoid immediate collapse when clicking a module
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isSidebarExpanded]);

  return (
    <Box
      id="sidebar-container"
      sx={{
        width: sidebarWidth,
        borderRight: "1px solid #eee",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bgcolor: "#fafafa",
        zIndex: 1300,
        transition: "width 0.3s ease",
        overflow: "hidden",
      }}
    >
      {/* Logo/Title - Show when expanded */}
      <Box sx={{ p: 2, borderBottom: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: isSidebarExpanded ? "flex-start" : "center" }}>
        <Typography
          className="sidebar-logo"
          variant="h6"
          sx={{
            fontWeight: 700,
            whiteSpace: "nowrap",
            opacity: isSidebarExpanded ? 1 : 0,
            width: isSidebarExpanded ? "auto" : 0,
            overflow: "hidden",
            transition: "opacity 0.2s ease, width 0.2s ease",
          }}
        >
          ValidAIte
        </Typography>
      </Box>

      {/* Back to Projects Button */}
      <Box sx={{ borderBottom: "1px solid #eee" }}>
        <Tooltip title={!isSidebarExpanded ? "Back to Projects" : ""} placement="right" arrow>
          <ListItemButton
            onClick={() => navigate("/projects")}
            sx={{
              py: 1.2,
              px: isSidebarExpanded ? 2 : 1.5,
              justifyContent: isSidebarExpanded ? "flex-start" : "center",
              transition: "padding 0.2s ease, justify-content 0.2s ease",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: isSidebarExpanded ? 40 : "auto",
                color: "#666",
                justifyContent: "center",
                transition: "min-width 0.2s ease",
              }}
            >
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText
              primary="Back to Projects"
              sx={{
                opacity: isSidebarExpanded ? 1 : 0,
                width: isSidebarExpanded ? "auto" : 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                transition: "opacity 0.2s ease, width 0.2s ease",
                ml: 1,
                "& .MuiListItemText-primary": {
                  fontWeight: 500,
                  color: "#666",
                  fontSize: "14px",
                }
              }}
            />
          </ListItemButton>
        </Tooltip>
      </Box>

      {/* Navigation List - Starts right below ValidAIte */}
      <List sx={{ pt: 0 }}>
        {modules.map(({ label, icon, path }) => {
          const isSelected = pathname.endsWith(`/${path}`);
          return (
            <Tooltip
              key={path}
              title={!isSidebarExpanded ? label : ""}
              placement="right"
              arrow
            >
              <ListItemButton
                selected={isSelected}
                onClick={() => handleModuleClick(path)}
                sx={{
                  py: 1.2,
                  px: isSidebarExpanded ? 2 : 1.5,
                  justifyContent: isSidebarExpanded ? "flex-start" : "center",
                  transition: "padding 0.2s ease, justify-content 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    borderLeft: "3px solid #1976d2",
                  },
                }}
                className="sidebar-item"
              >
                <ListItemIcon
                  className="sidebar-icon"
                  sx={{
                    minWidth: isSidebarExpanded ? 40 : "auto",
                    color: isSelected ? "#1976d2" : "inherit",
                    justifyContent: "center",
                    transition: "min-width 0.2s ease",
                  }}
                >
                  {icon}
                </ListItemIcon>
                <ListItemText
                  className="sidebar-text"
                  primary={label}
                  sx={{
                    opacity: isSidebarExpanded ? 1 : 0,
                    width: isSidebarExpanded ? "auto" : 0,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    transition: "opacity 0.2s ease, width 0.2s ease",
                    ml: 1,
                    "& .MuiListItemText-primary": {
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? "#1976d2" : "inherit",
                    }
                  }}
                />
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );
}

export default Sidebar;


